import { Filter, getFilterElementsDefault, Input, useWorkplaceState } from '@finbeat/react-toolkit';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import { changeOrderStatusItem, getOrderStatus, getOrderStatuses } from 'axios/orderStatusApi';
import useApi from 'hooks/useApi';
import { OrderStatus, OrderStatusTable } from 'models/api/orderStatus';
import {
    OrderStatusRolesProvider,
    useOrderStatusRolesState,
} from 'providers/OrderStatusRolesProvider';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BsFillHouseFill, BsFillPersonFill } from 'react-icons/bs';
import { useQueryClient } from 'react-query';
import { FormGroup, Spinner } from 'reactstrap';
import './OrderStatus.scss';
import OrderStatusChangeModal from './OrderStatusChangeModal';

const RoleName = ({ roleId }: { roleId: string }) => {
    const { orderStatusRoles, isFetching } = useOrderStatusRolesState();

    if (isFetching) {
        return <Spinner />;
    }

    const roleNameIdx = orderStatusRoles.findIndex((x) => x.value === roleId);
    const roleName = roleNameIdx > -1 ? orderStatusRoles[roleNameIdx].text : roleId;
    return <div>{roleName}</div>;
};

const OrderStatusWorkspace = () => {
    const { t } = useTranslation();

    const [isModalVisible, setModalVisible] = useState(false);
    const api = useApi();

    const queryClient = useQueryClient();

    return (
        <OrderStatusRolesProvider>
            <BOWorkplace<OrderStatusTable, OrderStatus, Filter>
                pageTitle="BackOfficeOrderStatuses"
                filterParams={{}}
                showFilter={false}
                initColumns={[
                    {
                        key: 'code',
                        name: <Trans i18nKey="BackOfficeStatus" />,
                        cellClass: 'normalLineHeight',
                        formatter({ row }) {
                            const { code, name, clientStatusName, toStatusName } = row;

                            return (
                                <div className="pt-2">
                                    <p>{code}</p>
                                    <p>
                                        <small className="text-muted">{name}</small>
                                    </p>
                                    {clientStatusName ? (
                                        <p className="text-muted">
                                            <span className="status-name">
                                                <small>
                                                    {clientStatusName
                                                        ? clientStatusName.trim()
                                                        : ''}{' '}
                                                </small>
                                            </span>
                                            <span className="status-icon svg-baseline">
                                                <BsFillPersonFill />
                                            </span>
                                        </p>
                                    ) : (
                                        <></>
                                    )}

                                    <p className="text-muted">
                                        <span className="status-name">
                                            <small>
                                                {toStatusName ? toStatusName.trim() : ''}{' '}
                                            </small>
                                        </span>
                                        <span className="status-icon svg-baseline">
                                            <BsFillHouseFill />
                                        </span>
                                    </p>
                                </div>
                            );
                        },
                    },
                    {
                        key: 'AdminFinality',
                        name: <Trans i18nKey="BackOfficeFinality" />,
                        cellClass: 'normalLineHeight',
                        sortable: false,
                        formatter({ row }) {
                            const { isFinal, isClientFinalStatus } = row;

                            return (
                                <FormGroup className="pt-2">
                                    <Input
                                        type="checkbox"
                                        checked={isFinal}
                                        disabled
                                        label={t('BackOfficeGenerally')}
                                    />
                                    <Input
                                        type="checkbox"
                                        checked={isClientFinalStatus}
                                        disabled
                                        label={t('BackOfficeForClient')}
                                    />
                                </FormGroup>
                            );
                        },
                    },
                    {
                        key: 'CommonFlags',
                        name: <Trans i18nKey="BackOfficeFlags" />,
                        cellClass: 'normalLineHeight',
                        sortable: false,
                        width: '30%',
                        formatter({ row }) {
                            const {
                                canEditOrder,
                                canUpdDocs,
                                canPassToScoring,
                                canEditServices,
                                canTransfToOper,
                                isImportant,
                                isAssign,
                            } = row;

                            return (
                                <FormGroup className="pt-2 order-status__flags-cell">
                                    <Input
                                        type="checkbox"
                                        checked={canEditOrder}
                                        disabled
                                        label={t('BackOfficeEditingOrder')}
                                    />
                                    <Input
                                        type="checkbox"
                                        checked={canUpdDocs}
                                        disabled
                                        label={t('BackOfficeUpdDocs')}
                                    />
                                    <Input
                                        type="checkbox"
                                        checked={canPassToScoring}
                                        disabled
                                        label={t('BackOfficePassToScoring')}
                                    />
                                    <Input
                                        type="checkbox"
                                        checked={canEditServices}
                                        disabled
                                        label={t('BackOfficeEditingAdditionalServices')}
                                    />
                                    <Input
                                        type="checkbox"
                                        checked={canTransfToOper}
                                        disabled
                                        label={t('BackOfficeTransfToOper')}
                                    />
                                    <Input
                                        type="checkbox"
                                        checked={isImportant}
                                        labelClassName="text-ellipsis"
                                        disabled
                                        label={t('BackOfficeIsImportant')}
                                    />
                                    <Input
                                        type="checkbox"
                                        checked={isAssign}
                                        disabled
                                        label={t('BackOfficeIsAssign')}
                                    />
                                </FormGroup>
                            );
                        },
                    },
                    {
                        key: 'CommonTimeouts',
                        name: <Trans i18nKey="BackOfficeTimeouts" />,
                        cellClass: 'normalLineHeight',
                        sortable: false,
                        formatter({ row }) {
                            const { timeouts } = row;

                            return (
                                <div className="pt-2">
                                    {timeouts.map(({ action, timeout }, idx) => (
                                        <div key={idx}>
                                            {action} ({timeout})
                                        </div>
                                    ))}
                                </div>
                            );
                        },
                    },
                    {
                        key: 'CommonRoles',
                        name: <Trans i18nKey="BackOfficeRoles" />,
                        cellClass: 'normalLineHeight',
                        sortable: false,
                        formatter({ row }) {
                            const { roles: roleIds = [] } = row;

                            return (
                                <div className="pt-2">
                                    {roleIds.map((roleId, idx) => (
                                        <RoleName roleId={roleId} key={idx} />
                                    ))}
                                </div>
                            );
                        },
                    },
                ]}
                storagePath="orderStatus/columns"
                lineHeight={195}
                fetchQueryFn={(filter) => getOrderStatuses(api, null, filter)}
                fetchFormat={(dataObject) => dataObject}
                getFilterElements={getFilterElementsDefault([])}
                showModal={() => setModalVisible(true)}
                showPagination={false}
                modal={{
                    component: (
                        <OrderStatusChangeModal
                            isOpen={isModalVisible}
                            toggle={() => setModalVisible(false)}
                        />
                    ),
                    addMutationFn: (item) => new Promise<OrderStatus>((resolve) => resolve(item)),
                    changeMutationFn: (item) => changeOrderStatusItem(api, item),
                    fetchCurrentItemQueryFn: (id) => getOrderStatus(api, `${id}`),
                }}
                deleteMutationFn={(id) => {
                    return new Promise<string>((resolve) => resolve(''));
                }}
                queryKey={['orderStatus']}
                queryClient={queryClient}
                fetchExcelQueryFn={(filter) => {
                    throw new Error('Function not implemented.');
                }}
            />
        </OrderStatusRolesProvider>
    );
};

const useOrderStatusWorkplaceState = () => useWorkplaceState<OrderStatus, OrderStatus>();

export default OrderStatusWorkspace;
export { useOrderStatusWorkplaceState };
